import React, { useMemo, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartResponsiveContainer } from "../../../components/ChartResponsiveContainer";
import { COLORS } from "../../../constants";
import type {
  RelatorioTemposRankingLinha,
  RelatorioTemposSetor,
  RelatorioTemposUsuario,
} from "../../../services/controleProducaoService";
import { formatTempoMs } from "../FichaDiaria/CronometroItemButtons";

const COR_PRODUCAO = COLORS.PRIMARY;
const COR_PAUSA = COLORS.WARNING_DARK;
const COR_MEDIO = COLORS.TEAL_DARK;

const TOP_N = 8;

type ChartCardProps = {
  title: string;
  subtitle?: string;
  children: ReactNode;
};

const ChartCard = ({ title, subtitle, children }: ChartCardProps) => (
  <div className="col-span-1 w-full min-w-0 overflow-hidden rounded-2xl border border-neutral-200/90 bg-white p-3 shadow-sm sm:p-5">
    <h3 className="text-sm font-semibold text-neutral-900">{title}</h3>
    {subtitle ? (
      <p className="mt-1 text-xs text-neutral-500">{subtitle}</p>
    ) : null}
    <div className="relative mt-4 h-[20rem] min-h-[20rem] w-full min-w-0 sm:h-[22rem] sm:min-h-[22rem]">
      {children}
    </div>
  </div>
);

function truncar(nome: string, max = 18): string {
  const t = nome.trim();
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}

function horas(ms: number): number {
  return Math.round((ms / 3_600_000) * 100) / 100;
}

function formatHorasTick(valor: number): string {
  if (!Number.isFinite(valor)) return "";
  if (valor === 0) return "0h";
  if (valor < 1) return `${Math.round(valor * 60)}min`;
  return `${valor.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}h`;
}

type PieTooltipProps = {
  active?: boolean;
  payload?: Array<{
    payload?: { name?: string; valueMs?: number; percentLabel?: string };
  }>;
};

const PieTooltip = ({ active, payload }: PieTooltipProps) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  if (p?.valueMs == null) return null;
  return (
    <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-neutral-900">{p.name}</p>
      <p className="mt-1 text-neutral-600">
        {formatTempoMs(p.valueMs)}
        {p.percentLabel ? (
          <span className="text-neutral-500"> ({p.percentLabel})</span>
        ) : null}
      </p>
    </div>
  );
};

type BarTooltipProps = {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number;
    color?: string;
    payload?: { fullName?: string; producaoMs?: number; pausaMs?: number };
  }>;
  label?: string;
};

const UsoBarTooltip = ({ active, payload, label }: BarTooltipProps) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-neutral-900">{row?.fullName ?? label}</p>
      <ul className="mt-1 space-y-0.5 text-neutral-600">
        {payload.map((item) => {
          const ms = item.name === "Pausa" ? row?.pausaMs : row?.producaoMs;
          return (
            <li key={item.name}>
              <span style={{ color: item.color }}>{item.name}</span>:{" "}
              <span className="font-medium text-neutral-900">
                {formatTempoMs(ms ?? 0)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

type MedioTooltipProps = {
  active?: boolean;
  payload?: Array<{
    payload?: { fullName?: string; valorMs?: number; detalhe?: string };
  }>;
};

const MedioTooltip = ({ active, payload }: MedioTooltipProps) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  if (p?.valorMs == null) return null;
  return (
    <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-neutral-900">{p.fullName}</p>
      <p className="mt-1 text-neutral-600">
        Média:{" "}
        <span className="font-medium text-neutral-900">
          {formatTempoMs(p.valorMs)}
        </span>
      </p>
      {p.detalhe ? (
        <p className="mt-0.5 text-neutral-500">{p.detalhe}</p>
      ) : null}
    </div>
  );
};

type EntregaTooltipProps = {
  active?: boolean;
  payload?: Array<{
    payload?: {
      fullName?: string;
      percentual?: number;
      detalhe?: string;
    };
  }>;
};

const EntregaTooltip = ({ active, payload }: EntregaTooltipProps) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  if (p?.percentual == null) return null;
  return (
    <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-neutral-900">{p.fullName}</p>
      <p className="mt-1 text-neutral-600">
        Entrega:{" "}
        <span className="font-medium text-neutral-900">{p.percentual}%</span>
      </p>
      {p.detalhe ? (
        <p className="mt-0.5 text-neutral-500">{p.detalhe}</p>
      ) : null}
    </div>
  );
};

function corEntrega(pct: number): string {
  if (pct >= 90) return COLORS.SUCCESS;
  if (pct >= 70) return COLORS.WARNING_DARK;
  return COLORS.ERROR;
}

export type RelatorioTemposChartsProps = {
  usuarios: RelatorioTemposUsuario[];
  setores: RelatorioTemposSetor[];
  usuariosMaisRapidos: RelatorioTemposRankingLinha[];
};

const RelatorioTemposCharts: React.FC<RelatorioTemposChartsProps> = ({
  usuarios,
  setores,
  usuariosMaisRapidos,
}) => {
  const totais = useMemo(() => {
    const producaoMs = usuarios.reduce((acc, u) => acc + u.tempoTotalMs, 0);
    const pausaMs = usuarios.reduce((acc, u) => acc + (u.tempoPausaMs ?? 0), 0);
    return { producaoMs, pausaMs, totalMs: producaoMs + pausaMs };
  }, [usuarios]);

  const pieData = useMemo(() => {
    const { producaoMs, pausaMs, totalMs } = totais;
    if (totalMs <= 0) return [];
    const pct = (ms: number) => `${((ms / totalMs) * 100).toFixed(0)}%`;
    return [
      {
        key: "producao",
        name: "Produção",
        value: horas(producaoMs),
        valueMs: producaoMs,
        percentLabel: pct(producaoMs),
        fill: COR_PRODUCAO,
      },
      {
        key: "pausa",
        name: "Pausa",
        value: horas(pausaMs),
        valueMs: pausaMs,
        percentLabel: pct(pausaMs),
        fill: COR_PAUSA,
      },
    ].filter((item) => item.valueMs > 0);
  }, [totais]);

  const usoUsuarios = useMemo(() => {
    return [...usuarios]
      .filter((u) => u.tempoTotalMs > 0 || (u.tempoPausaMs ?? 0) > 0)
      .sort(
        (a, b) =>
          b.tempoTotalMs +
          (b.tempoPausaMs ?? 0) -
          (a.tempoTotalMs + (a.tempoPausaMs ?? 0))
      )
      .slice(0, TOP_N)
      .map((u) => ({
        name: truncar(u.userNome),
        fullName: u.userNome,
        Produção: horas(u.tempoTotalMs),
        Pausa: horas(u.tempoPausaMs ?? 0),
        producaoMs: u.tempoTotalMs,
        pausaMs: u.tempoPausaMs ?? 0,
      }));
  }, [usuarios]);

  const usoSetores = useMemo(() => {
    return [...setores]
      .filter((s) => s.tempoTotalMs > 0 || (s.tempoPausaMs ?? 0) > 0)
      .sort(
        (a, b) =>
          b.tempoTotalMs +
          (b.tempoPausaMs ?? 0) -
          (a.tempoTotalMs + (a.tempoPausaMs ?? 0))
      )
      .slice(0, TOP_N)
      .map((s) => ({
        name: truncar(s.setorNome),
        fullName: s.setorNome,
        Produção: horas(s.tempoTotalMs),
        Pausa: horas(s.tempoPausaMs ?? 0),
        producaoMs: s.tempoTotalMs,
        pausaMs: s.tempoPausaMs ?? 0,
      }));
  }, [setores]);

  const mediaUsuarios = useMemo(() => {
    return usuariosMaisRapidos.slice(0, TOP_N).map((row) => ({
      name: truncar(row.nome),
      fullName: row.nome,
      mediaHoras: horas(row.valor),
      valorMs: row.valor,
      detalhe: row.detalhe,
    }));
  }, [usuariosMaisRapidos]);

  const entregaSetores = useMemo(() => {
    return [...setores]
      .filter((s) => (s.itensEsperados ?? 0) > 0)
      .sort((a, b) => (b.percentualEntrega ?? 0) - (a.percentualEntrega ?? 0))
      .slice(0, TOP_N)
      .map((s) => ({
        name: truncar(s.setorNome),
        fullName: s.setorNome,
        percentual: s.percentualEntrega ?? 0,
        detalhe: `${s.itensEntregues ?? 0}/${s.itensEsperados ?? 0} itens`,
        fill: corEntrega(s.percentualEntrega ?? 0),
      }));
  }, [setores]);

  const temUso = pieData.length > 0;

  if (
    !temUso &&
    usoUsuarios.length === 0 &&
    mediaUsuarios.length === 0 &&
    entregaSetores.length === 0
  ) {
    return null;
  }

  return (
    <section aria-label="Gráficos de uso do tempo" className="min-w-0">
      <h2 className="mb-1 text-sm font-semibold text-neutral-800">
        Gráficos de uso do tempo
      </h2>
      <p className="mb-3 text-xs text-neutral-500">
        Produção é o relógio andando, sem contar a mesma hora duas vezes quando
        há mais de um item. Pausa (botão ou troca de item) fica separada — não
        entra na média nem no ritmo.
      </p>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard
          title="Uso do tempo no período"
          subtitle="Quanto do cronômetro foi produção e quanto ficou pausado."
        >
          {pieData.length > 0 ? (
            <ChartResponsiveContainer>
              <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="46%"
                  innerRadius="38%"
                  outerRadius="70%"
                  paddingAngle={1.5}
                  labelLine={false}
                  label={({ percent }) =>
                    (percent ?? 0) >= 0.06
                      ? `${((percent ?? 0) * 100).toFixed(0)}%`
                      : ""
                  }
                >
                  {pieData.map((item) => (
                    <Cell
                      key={item.key}
                      fill={item.fill}
                      stroke={COLORS.WHITE}
                      strokeWidth={1}
                    />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <Legend
                  layout="horizontal"
                  align="center"
                  verticalAlign="bottom"
                  iconSize={10}
                  wrapperStyle={{ fontSize: "11px", paddingTop: 8 }}
                />
              </PieChart>
            </ChartResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-neutral-500">
              Sem tempo cronometrado no recorte.
            </div>
          )}
        </ChartCard>

        <ChartCard
          title="Uso por usuário"
          subtitle="Horas de trabalho no período, sem sobrepor cronômetros. Pausa à parte."
        >
          {usoUsuarios.length > 0 ? (
            <ChartResponsiveContainer>
              <BarChart
                data={usoUsuarios}
                margin={{ top: 8, right: 8, left: 0, bottom: 32 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={COLORS.NEUTRAL_200}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={48}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatHorasTick}
                />
                <Tooltip content={<UsoBarTooltip />} />
                <Legend wrapperStyle={{ fontSize: "11px" }} />
                <Bar
                  dataKey="Produção"
                  stackId="uso"
                  fill={COR_PRODUCAO}
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="Pausa"
                  stackId="uso"
                  fill={COR_PAUSA}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ChartResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-neutral-500">
              Nenhum usuário com cronômetro no recorte.
            </div>
          )}
        </ChartCard>

        <ChartCard
          title="Uso por setor"
          subtitle="Mesma regra: produção empilhada com pausa, sem misturar as duas."
        >
          {usoSetores.length > 0 ? (
            <ChartResponsiveContainer>
              <BarChart
                data={usoSetores}
                margin={{ top: 8, right: 8, left: 0, bottom: 32 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={COLORS.NEUTRAL_200}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={48}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatHorasTick}
                />
                <Tooltip content={<UsoBarTooltip />} />
                <Legend wrapperStyle={{ fontSize: "11px" }} />
                <Bar dataKey="Produção" stackId="uso" fill={COR_PRODUCAO} />
                <Bar
                  dataKey="Pausa"
                  stackId="uso"
                  fill={COR_PAUSA}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ChartResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-neutral-500">
              Nenhum setor com cronômetro no recorte.
            </div>
          )}
        </ChartCard>

        <ChartCard
          title="Menor tempo médio — usuários"
          subtitle="Só tempo de produção. Quem encerra cada cronometragem mais rápido."
        >
          {mediaUsuarios.length > 0 ? (
            <ChartResponsiveContainer>
              <BarChart
                data={mediaUsuarios}
                layout="vertical"
                margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={COLORS.NEUTRAL_200}
                />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatHorasTick}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={64}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={<MedioTooltip />} />
                <Bar
                  dataKey="mediaHoras"
                  name="Tempo médio"
                  fill={COR_MEDIO}
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ChartResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-neutral-500">
              Sem média de produção no recorte.
            </div>
          )}
        </ChartCard>

        {entregaSetores.length > 0 ? (
          <ChartCard
            title="Entrega por setor"
            subtitle="Itens lançados na ficha (produziu ou não produzido) sobre o que o setor deveria entregar no período."
          >
            <ChartResponsiveContainer>
              <BarChart
                data={entregaSetores}
                layout="vertical"
                margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={COLORS.NEUTRAL_200}
                />
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `${v}%`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={64}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={<EntregaTooltip />} />
                <Bar dataKey="percentual" name="Entrega" radius={[0, 4, 4, 0]}>
                  {entregaSetores.map((item) => (
                    <Cell key={item.fullName} fill={item.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ChartResponsiveContainer>
          </ChartCard>
        ) : null}
      </div>
    </section>
  );
};

export default RelatorioTemposCharts;
